import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid2 as Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import axios from "axios";
import validator from "validator";
import { handlePhoneInputChange as formatPhone } from "../../functions/permitFormHelpers";
import EnterpriseNotariesGrid from "../enterprise/EnterpriseNotariesGrid";

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

const OverviewMetricCard = ({ label, value, helper }) => (
  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {!!helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {helper}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Branches = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const largePC = useMediaQuery(theme.breakpoints.up("xl"));

  const isEnterprise = Boolean(currentUser?.user?.enterprise?.is_enterprise);
  const [activeTab, setActiveTab] = useState(0);
  const [createBranchLoading, setCreateBranchLoading] = useState(false);
  const [branchFormErrors, setBranchFormErrors] = useState({});
  const [branchRequestError, setBranchRequestError] = useState("");
  const [branchRequestSuccess, setBranchRequestSuccess] = useState("");
  const [branchUsers, setBranchUsers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchDetailsOpen, setBranchDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [deleteBranchLoading, setDeleteBranchLoading] = useState(false);
  const [branchForm, setBranchForm] = useState({
    branchName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    licenseType: "",
    licenseNumber: "",
    businessName: "",
  });

  useEffect(() => {
    if (!isEnterprise) {
      navigate("/login");
    }
  }, [isEnterprise, navigate]);

  const personal = currentUser?.user?.personal?.[0] || {};
  const plan = currentUser?.tier || currentUser?.user?.plan || {};
  const enterpriseMeta = currentUser?.user?.enterprise || {};
  const analytics = currentUser?.user?.analytics || {};
  const selectedCounties = currentUser?.user?.selectedCountiesNOC || [];
  const enterpriseLicenses = useMemo(
    () => currentUser?.user?.contractors ?? [],
    [currentUser?.user?.contractors],
  );

  // const initialBranchUsers = useMemo(() => {
  //   const enterpriseBranches = currentUser?.user?.enterprise?.branches;
  //   const rootBranches = currentUser?.user?.branches;

  //   if (Array.isArray(enterpriseBranches)) return enterpriseBranches;
  //   if (Array.isArray(rootBranches)) return rootBranches;
  //   return [];
  // }, [currentUser]);

  // useEffect(() => {
  //   setBranchUsers(initialBranchUsers);
  // }, [initialBranchUsers]);

  const fetchBranches = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/list`,
      { currentSession },
      { withCredentials: true },
    );

    if (response?.data?.success) {
      setBranchUsers(response.data.data || []);
    }
  };

  const handleOpenBranchDetails = (branch) => {
    setSelectedBranch(branch);
    setBranchDetailsOpen(true);
  };

  const handleRequestDeleteBranch = (branch) => {
    setBranchRequestError("");
    setBranchToDelete(branch);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBranch = async () => {
    if (!branchToDelete?.email) return;

    try {
      setDeleteBranchLoading(true);
      setBranchRequestError("");

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/delete`,
        {
          currentSession,
          email: branchToDelete.email,
        },
        { withCredentials: true },
      );

      if (response?.data?.success) {
        setBranchUsers(response.data.data || []);
        setDeleteDialogOpen(false);
        if (selectedBranch?.email === branchToDelete.email) {
          setSelectedBranch(null);
          setBranchDetailsOpen(false);
        }
        setBranchToDelete(null);
        setBranchRequestSuccess("Branch removed successfully.");
      }
    } catch (error) {
      setBranchRequestError(
        error?.response?.data?.message || "Failed to delete branch user.",
      );
    } finally {
      setDeleteBranchLoading(false);
    }
  };

  useEffect(() => {
    if (isEnterprise) {
      fetchBranches().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnterprise]);

  const branchGridRows = useMemo(
    () =>
      branchUsers.map((branch, index) => ({
        id: branch?.branch_id || branch?.email || index,
        branchName:
          branch?.branch_name ||
          branch?.additional?.companyName ||
          `${branch?.personal?.[0]?.firstName || ""} ${branch?.personal?.[0]?.lastName || ""}`.trim() ||
          branch?.email ||
          `Branch ${index + 1}`,
        email: branch?.email || "",
        phone: branch?.personal?.[0]?.phone || "N/A",
        createdAt: branch?.createdAt
          ? new Date(branch.createdAt * 1000).toLocaleDateString()
          : "N/A",
        raw: branch,
      })),
    [branchUsers],
  );

  const branchColumns = useMemo(
    () => [
      {
        field: "branchName",
        headerName: "Branch Name",
        flex: 1.4,
        minWidth: 150,
      },
      { field: "email", headerName: "Email", flex: 1.6, minWidth: 170 },
      { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
      { field: "createdAt", headerName: "Created", flex: 0.9, minWidth: 90 },
      // {
      //   field: "actions",
      //   headerName: "Actions",
      //   sortable: false,
      //   filterable: false,
      //   width: 80,
      //   minWidth: 80,
      //   renderCell: (params) => (
      //     <IconButton
      //       color="error"
      //       onClick={(event) => {
      //         event.stopPropagation();
      //         handleRequestDeleteBranch(params.row.raw);
      //       }}
      //     >
      //       <DeleteOutlineIcon />
      //     </IconButton>
      //   ),
      // },
    ],
    [],
  );

  const formatBranchDate = (value) => {
    if (!value || value === "N/A") return "N/A";

    if (typeof value === "number") {
      return new Date(value * 1000).toLocaleDateString();
    }

    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString();
    }

    return String(value);
  };

  const getShortBranchId = (branchId) => {
    const normalized = String(branchId || "").trim();
    if (!normalized) return "N/A";
    return normalized.slice(-6);
  };

  const overviewMetrics = [
    {
      label: "Enterprise ID",
      value: getShortBranchId(enterpriseMeta?.enterprise_id),
      helper: "Primary organization identifier",
    },
    {
      label: "Branch Count",
      value: branchUsers.length,
      helper: "Active linked branch accounts",
    },
    {
      label: "License Allowance",
      value: plan?.license || analytics?.contractors_limit || "N/A",
      helper: "Total company licenses available",
    },
    {
      label: "County Access",
      value: selectedCounties.length,
      helper: "Counties currently available to the organization",
    },
    {
      label: "Downloads",
      value: plan?.downloads || analytics?.availableDownloads || "N/A",
      helper: "Plan download allowance",
    },
    {
      label: "Base Plan",
      value: plan?.title || currentUser?.user?.plan?.title || "Enterprise",
      helper: "Current enterprise plan",
    },
  ];

  const selectedBranchPersonal = selectedBranch?.personal?.[0] || {};
  const selectedBranchNotaries = useMemo(
    () => selectedBranch?.notaries || [],
    [selectedBranch?.notaries],
  );
  const selectedBranchContractors = useMemo(
    () => selectedBranch?.contractors || [],
    [selectedBranch?.contractors],
  );
  const pendingInvitesCount = branchUsers.filter(
    (branch) => !branch?.branch_registered,
  ).length;
  const activeBranchesCount = branchUsers.filter(
    (branch) => branch?.branch_registered,
  ).length;

  const contractorDetailRows = useMemo(
    () =>
      selectedBranchContractors.map((contractor, index) => ({
        id:
          contractor?.licenseNumber ||
          contractor?._id?.$oid ||
          contractor?._id ||
          index,
        businessName:
          contractor?.businessName ||
          contractor?.dba_name ||
          contractor?.name ||
          "N/A",
        licenseNumber: contractor?.licenseNumber || "N/A",
        licenseType:
          contractor?.licenseType ||
          contractor?.rank ||
          contractor?.type ||
          "N/A",
        email: contractor?.email || "N/A",
        phone: contractor?.mobile || contractor?.phone || "N/A",
      })),
    [selectedBranchContractors],
  );

  const contractorDetailColumns = useMemo(
    () => [
      {
        field: "businessName",
        headerName: "Business",
        flex: 1.4,
        minWidth: 180,
      },
      {
        field: "licenseNumber",
        headerName: "License #",
        flex: 1,
        minWidth: 140,
      },
      { field: "licenseType", headerName: "Type", flex: 1, minWidth: 140 },
      { field: "email", headerName: "Email", flex: 1.2, minWidth: 180 },
      { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
    ],
    [],
  );

  const notaryDetailRows = useMemo(
    () =>
      selectedBranchNotaries.map((notary, index) => ({
        id: notary?.notary_id || notary?.email || index,
        full_name: notary?.full_name || "N/A",
        email: notary?.email || "N/A",
        phone: notary?.phone || "N/A",
        commission_number: notary?.commission_number || "N/A",
        commission_expiry: formatBranchDate(notary?.commission_expiry),
        status: notary?.status || "Invitation Sent",
      })),
    [selectedBranchNotaries],
  );

  const notaryDetailColumns = useMemo(
    () => [
      { field: "full_name", headerName: "Name", flex: 1.2, minWidth: 170 },
      { field: "email", headerName: "Email", flex: 1.3, minWidth: 190 },
      { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
      {
        field: "commission_number",
        headerName: "Commission #",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "commission_expiry",
        headerName: "Expiry",
        flex: 0.9,
        minWidth: 120,
      },
      { field: "status", headerName: "Status", flex: 0.9, minWidth: 140 },
    ],
    [],
  );

  const handleBranchFormChange = (event) => {
    const { name, value } = event.target;
    setBranchForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const phoneDigits = branchForm.phone.replace(/\D/g, "");
  const isBranchFormComplete = Boolean(
    branchForm.branchName?.trim() &&
      branchForm.firstName?.trim() &&
      branchForm.lastName?.trim() &&
      branchForm.email?.trim() &&
      branchForm.phone?.trim() &&
      validator.isEmail(branchForm.email || "") &&
      phoneDigits.length === 10,
  );

  const validateBranchForm = () => {
    const nextErrors = {};

    if (!branchForm.branchName?.trim())
      nextErrors.branchName = "Branch name is required";
    if (!branchForm.firstName?.trim())
      nextErrors.firstName = "First name is required";
    if (!branchForm.lastName?.trim())
      nextErrors.lastName = "Last name is required";
    if (!branchForm.email?.trim()) nextErrors.email = "Email is required";
    else if (!validator.isEmail(branchForm.email))
      nextErrors.email = "Email must be valid";
    if (!branchForm.phone?.trim()) nextErrors.phone = "Phone is required";
    else if (phoneDigits.length !== 10)
      nextErrors.phone = "Phone must be a valid 10-digit mobile number";

    setBranchFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateBranch = async () => {
    setBranchRequestError("");
    setBranchRequestSuccess("");

    if (!validateBranchForm()) return;

    try {
      setCreateBranchLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/create`,
        {
          currentSession,
          values: {
            ...branchForm,
            email: branchForm.email.trim().toLowerCase(),
          },
        },
        { withCredentials: true },
      );

      if (response?.data?.success) {
        setBranchUsers(response.data.data || []);
        setBranchForm({
          branchName: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          licenseType: "",
          licenseNumber: "",
          businessName: "",
        });
        setBranchFormErrors({});
        setBranchRequestSuccess("Branch invitation sent successfully.");
        setActiveTab(1);
      }
    } catch (error) {
      setBranchRequestError(
        error?.response?.data?.message || "Failed to create branch user.",
      );
    } finally {
      setCreateBranchLoading(false);
    }
  };

  if (!isEnterprise) {
    return null;
  }

  return (
    <Box
      sx={{
        mx: largePC ? "auto" : 0,
        maxWidth: "1700px",
        py: 1,
        backgroundColor: "#EEF2F6",
        overflowX: "hidden",
        mb: tabletSize ? "5rem" : undefined,
        mt: 1,
      }}
    >
      <Grid
        container
        sx={{
          px: mobileSize ? 1 : tabletSize ? 2 : 3,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: "1500px",
            mx: "auto",
            p: mobileSize ? 2 : 3,
            borderRadius: 3,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h4" color="primary" gutterBottom>
              Branches
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 900, mx: "auto" }}
            >
              Manage your enterprise organization, review linked branches, and
              prepare new branch accounts that will share company access and
              licenses.
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="Enterprise Overview" />
              <Tab label={`Branches (${branchUsers.length})`} />
              <Tab label="Notaries" />
              <Tab label="Add Branch" />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={2.5}>
              {overviewMetrics.map((metric) => (
                <Grid key={metric.label} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <OverviewMetricCard {...metric} />
                </Grid>
              ))}

              <Grid size={{ xs: 12, lg: 7 }}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Enterprise Account Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Account Holder
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {(personal?.firstName || "").trim()}{" "}
                          {(personal?.lastName || "").trim()}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {currentUser?.user?.email || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {personal?.phone || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Timezone
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {personal?.timezone || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      <Chip label="Enterprise Owner" color="primary" />
                      <Chip
                        label={`${selectedCounties.length} Counties Enabled`}
                        variant="outlined"
                      />
                      <Chip
                        label={`${enterpriseLicenses.length} Company Licenses`}
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, lg: 5 }}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Shared Enterprise Access
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      All branches created under this enterprise account are
                      expected to inherit organization-level access, selected
                      counties, and shared company licenses.
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom>
                      Current shared counties preview
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedCounties.slice(0, 10).map((county) => (
                        <Chip
                          key={county?.name}
                          label={county?.name || "County"}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))}
                      {selectedCounties.length > 10 && (
                        <Chip
                          label={`+${selectedCounties.length - 10} more`}
                          size="small"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {branchUsers.length === 0 ? (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 2, lg: 2 } }}>
                  <Card sx={{ width: "100%", borderRadius: 3, boxShadow: 2 }}>
                    <CardContent>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No branches have been linked yet.
                      </Alert>
                      <Typography
                        sx={{ textAlign: "center" }}
                        color="text.secondary"
                      >
                        Once branch accounts are created, they will appear here
                        in a management grid.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 1, lg: 1 } }}>
                  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Summary
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        When branch accounts are created, this panel will show a
                        quick overview of branch activity and status.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 2, lg: 2 } }}>
                  <Card sx={{ width: "100%", borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Management
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Click a row to view branch details.
                      </Typography>
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: 420,
                          maxWidth: "100%",
                          overflowX: "hidden",
                        }}
                      >
                        <DataGrid
                          rows={branchGridRows}
                          columns={branchColumns}
                          disableRowSelectionOnClick
                          disableColumnMenu
                          disableColumnResize
                          pageSizeOptions={[5, 10, 25]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 5, page: 0 },
                            },
                          }}
                          onRowClick={(params) =>
                            handleOpenBranchDetails(params.row.raw)
                          }
                          sx={{
                            width: "100%",
                            maxWidth: "100%",
                            border: "1px solid #FCF2F0",
                            overflowX: "hidden",
                            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
                              {
                                outline: "none !important",
                              },
                            "& .MuiDataGrid-columnHeader:focus-within": {
                              outline: "none !important",
                            },
                            "& .MuiDataGrid-columnHeaders": { padding: 0 },
                            "& .MuiDataGrid-columnHeadersInner": {
                              width: "100%",
                            },
                            "& .MuiDataGrid-cell": { borderColor: "#333333" },
                            "& .MuiDataGrid-columnSeparator": {
                              display: "none",
                            },
                            "& .MuiDataGrid-row": { cursor: "pointer" },
                            "& .MuiDataGrid-main": {
                              overflowX: "hidden",
                            },
                            "& .MuiDataGrid-virtualScroller": {
                              overflowX: "hidden !important",
                            },
                            "& .MuiDataGrid-footerContainer": {
                              overflowX: "hidden",
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 1, lg: 1 } }}>
                  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Summary
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Branches
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {branchUsers.length}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Pending Invites
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {pendingInvitesCount}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Active Branches
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {activeBranchesCount}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Shared Licenses
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {enterpriseLicenses.length}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box
                        sx={{
                          mt: 3,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={`${branchUsers.length} Branches`}
                          color="primary"
                        />
                        <Chip
                          label={`${pendingInvitesCount} Pending`}
                          variant="outlined"
                        />
                        <Chip
                          label={`${activeBranchesCount} Active`}
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }} sx={{ maxWidth: "100%", minWidth: 0 }}>
                <EnterpriseNotariesGrid
                  currentSession={currentSession}
                  currentUser={currentUser}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Add New Branch
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Create a new branch user under this enterprise account.
                    </Typography>

                    <Grid container spacing={2}>
                      {!!branchRequestError && (
                        <Grid size={{ xs: 12 }}>
                          <Alert severity="error">{branchRequestError}</Alert>
                        </Grid>
                      )}
                      {!!branchRequestSuccess && (
                        <Grid size={{ xs: 12 }}>
                          <Alert severity="success">
                            {branchRequestSuccess}
                          </Alert>
                        </Grid>
                      )}
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Branch Name"
                          name="branchName"
                          value={branchForm.branchName}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.branchName)}
                          helperText={branchFormErrors.branchName || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Branch Manager First Name"
                          name="firstName"
                          value={branchForm.firstName}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.firstName)}
                          helperText={branchFormErrors.firstName || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Branch Manager Last Name"
                          name="lastName"
                          value={branchForm.lastName}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.lastName)}
                          helperText={branchFormErrors.lastName || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Branch Manager Email"
                          name="email"
                          value={branchForm.email}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.email)}
                          helperText={branchFormErrors.email || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Branch Manager Phone"
                          name="phone"
                          value={branchForm.phone}
                          onChange={(e) => {
                            const formatted = formatPhone(e);
                            setBranchForm((prev) => ({
                              ...prev,
                              phone: formatted,
                            }));
                          }}
                          error={Boolean(branchFormErrors.phone)}
                          helperText={branchFormErrors.phone || ""}
                        />
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 3,
                      }}
                    >
                      <LoadingButton
                        variant="contained"
                        loading={createBranchLoading}
                        disabled={!isBranchFormComplete || createBranchLoading}
                        onClick={handleCreateBranch}
                      >
                        Create Branch
                      </LoadingButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>

        <Drawer
          anchor="right"
          open={branchDetailsOpen}
          onClose={() => setBranchDetailsOpen(false)}
        >
          <Box
            sx={{
              width: { xs: "100vw", sm: 860, md: 1080 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                {selectedBranch?.branch_name || "Branch Details"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedBranch?.email || ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                <Chip
                  label={`Contractors ${selectedBranchContractors.length}`}
                  variant="outlined"
                />
                <Chip
                  label={`Notaries ${selectedBranchNotaries.length}`}
                  variant="outlined"
                />
                <Chip
                  label={`NOC ${selectedBranch?.analytics?.nocCreated ?? 0}`}
                  variant="outlined"
                />
                <Chip
                  label={`PP ${selectedBranch?.analytics?.ppDownloaded ?? 0}`}
                  variant="outlined"
                />
                <Chip
                  label={`Downloads ${selectedBranch?.analytics?.availableDownloads ?? "N/A"}`}
                  variant="outlined"
                />
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Branch Name
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranch?.branch_name || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Branch ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {getShortBranchId(selectedBranch?.branch_id)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranch?.email || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Created At
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatBranchDate(selectedBranch?.createdAt)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Registration Status
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranch?.branch_registered
                              ? "Active"
                              : "Invitation Pending"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Available Downloads
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranch?.analytics?.availableDownloads ??
                              "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Manager
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            First Name
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranchPersonal?.firstName || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Last Name
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranchPersonal?.lastName || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranchPersonal?.phone || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Timezone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBranchPersonal?.timezone || "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Contractors
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {contractorDetailRows.length === 0 ? (
                        <Alert severity="warning">
                          This branch does not have any linked contractors.
                        </Alert>
                      ) : (
                        <Box sx={{ width: "100%", minHeight: 260 }}>
                          <DataGrid
                            rows={contractorDetailRows}
                            columns={contractorDetailColumns}
                            autoHeight
                            disableRowSelectionOnClick
                            disableColumnMenu
                            pageSizeOptions={[5, 10]}
                            initialState={{
                              pagination: {
                                paginationModel: { pageSize: 5, page: 0 },
                              },
                            }}
                            sx={{
                              border: "1px solid #FCF2F0",
                              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
                                {
                                  outline: "none !important",
                                },
                              "& .MuiDataGrid-columnHeader:focus-within": {
                                outline: "none !important",
                              },
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Notaries
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {notaryDetailRows.length === 0 ? (
                        <Alert severity="info">
                          No notaries are currently linked to this branch.
                        </Alert>
                      ) : (
                        <Box sx={{ width: "100%", minHeight: 260 }}>
                          <DataGrid
                            rows={notaryDetailRows}
                            columns={notaryDetailColumns}
                            autoHeight
                            disableRowSelectionOnClick
                            disableColumnMenu
                            pageSizeOptions={[5, 10]}
                            initialState={{
                              pagination: {
                                paginationModel: { pageSize: 5, page: 0 },
                              },
                            }}
                            sx={{
                              border: "1px solid #FCF2F0",
                              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
                                {
                                  outline: "none !important",
                                },
                              "& .MuiDataGrid-columnHeader:focus-within": {
                                outline: "none !important",
                              },
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                bgcolor: "background.paper",
                borderTop: "1px solid #e0e0e0",
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={() => setBranchDetailsOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Drawer>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setBranchRequestError("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Branch User</DialogTitle>
          <DialogContent>
            {!!branchRequestError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {branchRequestError}
              </Alert>
            )}
            <Typography>
              Are you sure you want to permanently delete{" "}
              <strong>
                {branchToDelete?.branch_name ||
                  branchToDelete?.email ||
                  "this branch"}
              </strong>
              ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              loading={deleteBranchLoading}
              onClick={handleDeleteBranch}
            >
              Delete Branch
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Grid>
    </Box>
  );
};

export default Branches;
